import React from 'react';
import { ArrowLeftIcon, ArrowRightIcon, XIcon } from '@phosphor-icons/react';
import Image from '@plone/volto/components/theme/Image/Image';

import {
  contador,
  ESCALA_AMPLIADA,
  ESCALA_MINIATURA,
  getGaleriaImageProps,
  type GaleriaFoto,
} from './helpers';

interface LightboxProps {
  aberto: boolean;
  fotos: GaleriaFoto[];
  indice: number;
  mostrarCredito: boolean;
  onFechar: () => void;
  onAnterior: () => void;
  onProxima: () => void;
  onSelecionar: (indice: number) => void;
}

/**
 * Foto ampliada, no `<dialog>` nativo.
 *
 * `showModal()` entrega de graça o que um overlay `position: fixed` teria que
 * reimplementar em JS: trava de foco, Esc, bloqueio do scroll do fundo e o véu
 * (`::backdrop`). Mesma mecânica de `View/CapaView/YoutubeDialog.tsx` e do
 * `MPFModal`. Sobram três coisas para fazer à mão: as setas do teclado, o
 * clique no fundo e sincronizar o estado do React quando o Esc fecha sozinho.
 *
 * O elemento nasce sempre no DOM (fechado, já oculto pelo navegador), então
 * `aberto` é só um espelho — não há montagem condicional.
 */
export const Lightbox: React.FC<LightboxProps> = ({
  aberto,
  fotos,
  indice,
  mostrarCredito,
  onFechar,
  onAnterior,
  onProxima,
  onSelecionar,
}) => {
  const ref = React.useRef<HTMLDialogElement>(null);
  const painelRef = React.useRef<HTMLDivElement>(null);
  const trilhaRef = React.useRef<HTMLUListElement>(null);
  const foto = fotos[indice];

  /*
   * `montado` atrasa a saída em relação a `aberto`.
   *
   * A entrada é imediata; a saída precisa esperar a animação terminar antes de
   * o `<dialog>` fechar — senão o modal some de uma vez e não há o que animar.
   * `aberto` continua sendo a intenção de quem chama; `montado` é o que está
   * de fato na tela.
   */
  const [montado, setMontado] = React.useState(aberto);
  const [saindo, setSaindo] = React.useState(false);

  React.useEffect(() => {
    if (aberto) {
      setMontado(true);
      setSaindo(false);
      return undefined;
    }
    if (!montado) return undefined;

    setSaindo(true);

    const painel = painelRef.current;
    let encerrado = false;
    const encerrar = () => {
      if (encerrado) return;
      encerrado = true;
      setSaindo(false);
      setMontado(false);
    };

    painel?.addEventListener('animationend', encerrar, { once: true });
    // Rede de segurança: se a animação não rodar (navegador sem suporte, aba
    // em segundo plano, `prefers-reduced-motion` cortando a duração), o modal
    // não pode ficar preso aberto esperando um evento que não vem.
    const relogio = setTimeout(encerrar, 400);

    return () => {
      painel?.removeEventListener('animationend', encerrar);
      clearTimeout(relogio);
    };
  }, [aberto, montado]);

  // Espelha `montado` (e não `aberto`) no elemento. `showModal()` estoura se já
  // estiver aberto e `close()` dispara o evento `close` — daí os guardas em
  // `dialog.open`.
  React.useEffect(() => {
    const dialog = ref.current;
    // `showModal` não existe no jsdom antigo nem em navegador sem suporte: sem
    // ele o modal não abre, mas a galeria segue utilizável no palco.
    if (!dialog || typeof dialog.showModal !== 'function') return;
    if (montado && !dialog.open) dialog.showModal();
    if (!montado && dialog.open) dialog.close();
  }, [montado]);

  /*
   * Esc: vetar o fechamento nativo para que ele passe pelo mesmo caminho dos
   * outros — estado do React, animação de saída, e só então `close()`. Sem
   * isto o Esc fecharia na hora, seco, enquanto o X e o clique no fundo
   * saem suave.
   */
  React.useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return undefined;
    const aoCancelar = (evento: Event) => {
      evento.preventDefault();
      onFechar();
    };
    dialog.addEventListener('cancel', aoCancelar);
    return () => dialog.removeEventListener('cancel', aoCancelar);
  }, [onFechar]);

  /*
   * Clique fora da foto fecha.
   *
   * O teste é pelo NEGATIVO — "não é a foto nem um controle" — e não
   * `evento.target === dialog`, que é a receita usual de clique no backdrop.
   * Aqui ela não serviria: o painel ocupa a tela inteira (`h-full w-full`),
   * então o `::backdrop` fica inalcançável e o alvo é sempre um nó de dentro.
   * Era por isso que só o X fechava.
   *
   * Ouvinte imperativo, e não `onClick` no JSX, pelo mesmo motivo do
   * `YoutubeDialog`: a análise estática leria um `onClick` numa `<div>` como
   * clique em elemento não interativo.
   */
  React.useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return undefined;
    const aoClicar = (evento: MouseEvent) => {
      const alvo = evento.target as HTMLElement | null;
      // Setas, X e miniaturas têm ação própria; a foto é o conteúdo. O resto
      // do painel é "fora da imagem".
      if (alvo?.closest('button, img')) return;
      onFechar();
    };
    dialog.addEventListener('click', aoClicar);
    return () => dialog.removeEventListener('click', aoClicar);
  }, [onFechar]);

  // Setas do teclado. No próprio `<dialog>` (que tem o foco enquanto está
  // aberto), e não no `document`: assim o ouvinte não precisa checar se o
  // modal está em cartaz nem disputa as setas com o resto da página.
  React.useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return undefined;
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'ArrowLeft') {
        evento.preventDefault();
        onAnterior();
      }
      if (evento.key === 'ArrowRight') {
        evento.preventDefault();
        onProxima();
      }
    };
    dialog.addEventListener('keydown', aoTeclar);
    return () => dialog.removeEventListener('keydown', aoTeclar);
  }, [onAnterior, onProxima]);

  // Mantém a miniatura em cartaz visível na tira. `scrollIntoView` não existe
  // no jsdom, daí o guarda.
  React.useEffect(() => {
    if (!montado) return;
    const ativa = trilhaRef.current?.querySelector<HTMLElement>(
      '[aria-current="true"]',
    );
    ativa?.scrollIntoView?.({ block: 'nearest', inline: 'center' });
  }, [montado, indice]);

  const imageProps = foto ? getGaleriaImageProps(foto, ESCALA_AMPLIADA) : null;

  return (
    <dialog
      ref={ref}
      aria-label="Galeria de imagens ampliada"
      className={`mpf-galeria-lightbox${saindo ? ' mpf-galeria-lightbox--saindo' : ''}`}
      onClose={onFechar}
    >
      {/* O painel só existe enquanto o modal está em cartaz. Mesma escolha do
          `YoutubeDialog`, que só monta o iframe com vídeo escolhido: aqui o
          peso é a tira de miniaturas, uma imagem por foto da galeria, que não
          tem por que estar no DOM da página inteira o tempo todo. */}
      {montado && foto && (
        <div ref={painelRef} className="mpf-galeria-lightbox__painel">
          <div className="mpf-galeria-lightbox__topo">
            <span className="mpf-galeria-lightbox__contador">
              {contador(indice, fotos.length)}
            </span>
            <button
              type="button"
              onClick={onFechar}
              className="mpf-galeria-lightbox__fechar"
            >
              <XIcon aria-hidden="true" />
              <span className="sr-only">Fechar a foto ampliada</span>
            </button>
          </div>

          <div className="mpf-galeria-lightbox__palco">
            <button
              type="button"
              onClick={onAnterior}
              className="mpf-galeria-lightbox__seta"
            >
              <ArrowLeftIcon aria-hidden="true" />
              <span className="sr-only">Foto anterior</span>
            </button>

            {/* `flex-1 min-h-0` no pai + `object-contain` aqui: é o par que
                faz a foto caber na tela em vez de estourar e criar rolagem. */}
            <div className="mpf-galeria-lightbox__moldura">
              {imageProps && (
                <Image
                  /* Mesma mecânica do palco: a `key` remonta a imagem a cada
                     troca de foto, e a montagem dispara a animação. */
                  key={foto.key}
                  {...imageProps}
                  alt={foto.alt}
                  decoding="async"
                  className="mpf-galeria-lightbox__img"
                />
              )}
            </div>

            <button
              type="button"
              onClick={onProxima}
              className="mpf-galeria-lightbox__seta"
            >
              <ArrowRightIcon aria-hidden="true" />
              <span className="sr-only">Próxima foto</span>
            </button>
          </div>

          <div className="mpf-galeria-lightbox__rodape">
            {foto.legenda && (
              <p
                key={`legenda-${foto.key}`}
                className="mpf-galeria-lightbox__legenda"
              >
                {foto.legenda}
              </p>
            )}
            {mostrarCredito && foto.credito && (
              <p
                key={`credito-${foto.key}`}
                className="mpf-galeria-lightbox__credito"
              >
                {foto.credito}
              </p>
            )}

            <ul ref={trilhaRef} className="mpf-galeria-lightbox__tira">
              {fotos.map((item) => (
                <li key={item.key}>
                  <button
                    type="button"
                    onClick={() => onSelecionar(item.indice)}
                    aria-current={item.indice === indice ? 'true' : undefined}
                    aria-label={`Ver a foto ${item.numero}`}
                    className="mpf-galeria-lightbox__tira-item"
                  >
                    {getGaleriaImageProps(item, ESCALA_MINIATURA) && (
                      <Image
                        {...getGaleriaImageProps(item, ESCALA_MINIATURA)!}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        className="mpf-galeria-lightbox__tira-img"
                      />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </dialog>
  );
};

export default Lightbox;
